import {
    ConversationStatus,
    FeedbackType,
    PaperAgentGetArtifactsInput,
    PaperAgentGetArtifactsOutput,
    PaperAgentGetConversationInput,
    PaperAgentGetConversationOutput,
    PaperAgentGetStatusInput,
    PaperAgentGetStatusOutput,
    PaperAgentListConversationsInput,
    PaperAgentListConversationsOutput,
    PaperAgentSendMessageInput,
    PaperAgentSendMessageOutput,
    PaperAgentSubmitFeedbackInput,
    PaperAgentSubmitFeedbackOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';

import { Auth } from 'aws-amplify';
import { contractConfigurationClient } from 'src/api/client';

import { Artifact, Conversation, Message } from '../types';
import { compareTimestamps, fetchAllPages, getMaxTimestamp, timestampToNumber } from './helpers';

/**
 * Send a message to the Paper Agent
 * @param conversationId Optional conversation ID for existing conversations
 * @param user Username of the sender
 * @param message Message content
 * @returns Promise that resolves to the conversation ID
 */
export const sendMessage = async (
    conversationId: string | null,
    user: string,
    message: string,
    endpoint: string,
    toolkits: string[] | undefined
): Promise<string> => {
    const userSession = await Auth.currentSession();

    return new Promise<string>((resolve, reject) => {
        contractConfigurationClient
            .paperAgentSendMessage(
                PaperAgentSendMessageInput.clone({
                    conversationId: conversationId || undefined,
                    user,
                    message,
                    endpoint: endpoint,
                    toolkits: toolkits,
                    identities: {
                        ORIGAMI: userSession.getIdToken().getJwtToken()
                    }
                })
            )
            .subscribe(
                (response: PaperAgentSendMessageOutput) => {
                    resolve(response.conversationId);
                },
                (error: UnknownCoralError) => {
                    console.error('Error sending message:', error.receivedException.message);
                    reject(new Error(error.receivedException.message));
                }
            );
    });
};

/**
 * Get conversation messages with pagination support
 * @param conversationId Conversation ID to fetch
 * @param continuationToken Optional continuation token for pagination
 * @returns Promise that resolves to messages array
 */
export const getConversation = async (conversationId: string, continuationToken?: string): Promise<Message[]> => {
    return fetchAllPages(async (token?: string) => {
        return new Promise<{ data: Message[]; continuationToken?: string }>((resolve, reject) => {
            contractConfigurationClient
                .paperAgentGetConversation(
                    PaperAgentGetConversationInput.clone({
                        conversationId,
                        continuationToken: token || continuationToken
                    })
                )
                .subscribe(
                    (response: PaperAgentGetConversationOutput) => {
                        resolve({
                            data: Array.from(response.messages),
                            continuationToken: response.continuationToken
                        });
                    },
                    (error: UnknownCoralError) => {
                        console.error('Error getting conversation:', error.receivedException.message);
                        reject(new Error(error.receivedException.message));
                    }
                );
        });
    });
};

/**
 * List all conversations with pagination support
 * @param continuationToken Optional continuation token for pagination
 * @returns Promise that resolves to conversations array
 */
export const listConversations = async (continuationToken?: string): Promise<Conversation[]> => {
    return fetchAllPages(async (token?: string) => {
        return new Promise<{ data: Conversation[]; continuationToken?: string }>((resolve, reject) => {
            contractConfigurationClient
                .paperAgentListConversations(
                    PaperAgentListConversationsInput.clone({
                        continuationToken: token || continuationToken
                    })
                )
                .subscribe(
                    (response: PaperAgentListConversationsOutput) => {
                        resolve({
                            data: Array.from(response.conversations),
                            continuationToken: response.continuationToken
                        });
                    },
                    (error: UnknownCoralError) => {
                        console.error('Error listing conversations:', error.receivedException.message);
                        reject(new Error(error.receivedException.message));
                    }
                );
        });
    });
};

/**
 * Get artifacts for a specific message with pagination support
 * @param conversationId Conversation ID
 * @param timestamp Message timestamp (can be number or string to preserve precision)
 * @param continuationToken Optional continuation token for pagination
 * @returns Promise that resolves to artifacts array
 */
export const getArtifacts = async (
    conversationId: string,
    timestamp: string,
    continuationToken?: string
): Promise<Artifact[]> => {
    return fetchAllPages(async (token?: string) => {
        return new Promise<{ data: Artifact[]; continuationToken?: string }>((resolve, reject) => {
            contractConfigurationClient
                .paperAgentGetArtifacts(
                    PaperAgentGetArtifactsInput.clone({
                        conversationId,
                        timestamp: timestamp,
                        continuationToken: token || continuationToken
                    })
                )
                .subscribe(
                    (response: PaperAgentGetArtifactsOutput) => {
                        resolve({
                            data: Array.from(response.artifacts),
                            continuationToken: response.continuationToken
                        });
                    },
                    (error: UnknownCoralError) => {
                        console.error('Error getting artifacts:', error.receivedException.message);
                        reject(new Error(error.receivedException.message));
                    }
                );
        });
    });
};

/**
 * Filter conversations by current user
 * @param conversations Array of all conversations
 * @param currentUser Current user's username
 * @returns Filtered conversations for the current user
 */
export const filterConversationsByUser = (conversations: Conversation[], currentUser: string): Conversation[] => {
    return conversations.filter((conversation) => conversation.user === currentUser);
};

/**
 * Sort conversations by timestamp (newest first)
 * @param conversations Array of conversations to sort
 * @returns Sorted conversations array
 */
export const sortConversationsByTimestamp = (conversations: Conversation[]): Conversation[] => {
    return [...conversations].sort((a, b) => parseInt(b.timestamp) - parseInt(a.timestamp));
};

/**
 * Poll for new messages after sending a user message
 * Continues polling until a system message is received
 * @param conversationId Conversation ID to poll
 * @param lastKnownTimestamp Timestamp of the last known message to avoid duplicates
 * @param onNewMessages Callback for when new tool messages are received (display immediately)
 * @param onSystemMessage Callback when system message is received (final response, stop polling)
 * @param onError Callback for errors
 * @param intervalMs Polling interval in milliseconds (default 4000ms = 4 seconds)
 * @returns Function to stop polling
 */
export const pollForMessages = (
    conversationId: string,
    lastKnownTimestamp: string | number,
    onNewMessages: (messages: Message[]) => void,
    onSystemMessage: (systemMessage: Message) => void,
    onError: (error: Error) => void,
    intervalMs = 4000
): (() => void) => {
    let isPolling = true;
    let timeoutId: NodeJS.Timeout;
    let lastProcessedTimestamp = timestampToNumber(lastKnownTimestamp);

    const poll = async () => {
        if (!isPolling) return;

        try {
            const allMessages = await getConversation(conversationId);

            // Filter messages newer than the last processed timestamp
            const newMessages = allMessages.filter((msg) => timestampToNumber(msg.timestamp) > lastProcessedTimestamp);

            if (newMessages.length > 0) {
                // Sort messages by timestamp to process in order
                const sortedNewMessages = newMessages.sort((a, b) => compareTimestamps(a.timestamp, b.timestamp));

                // Check for system message (final response)
                const systemMessage = sortedNewMessages.find((msg) => msg.messageType === 'system');

                if (systemMessage) {
                    // Found system message - show any tool messages first, then system message, then stop polling
                    const toolMessages = sortedNewMessages.filter((msg) => msg.messageType === 'tool');

                    if (toolMessages.length > 0) {
                        onNewMessages(toolMessages);
                    }

                    // Stop polling and show final system message
                    isPolling = false;
                    onSystemMessage(systemMessage);
                    return;
                } else {
                    // Only tool messages - show them immediately and continue polling
                    const toolMessages = sortedNewMessages.filter((msg) => msg.messageType === 'tool');
                    if (toolMessages.length > 0) {
                        onNewMessages(toolMessages);
                        // Update last processed timestamp to avoid showing same messages again
                        lastProcessedTimestamp = getMaxTimestamp(toolMessages.map((msg) => msg.timestamp));
                    }
                }
            }

            // Schedule next poll
            if (isPolling) {
                timeoutId = setTimeout(poll, intervalMs);
            }
        } catch (error) {
            isPolling = false;
            onError(error instanceof Error ? error : new Error('Polling failed'));
        }
    };

    // Start polling
    timeoutId = setTimeout(poll, intervalMs);

    // Return stop function
    return () => {
        isPolling = false;
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    };
};

/**
 * Get the status of AI processing for a conversation
 * @param conversationId Conversation ID to check status for
 * @returns Promise that resolves to the status ("WORKING" or "READY")
 */
export const getConversationStatus = async (conversationId: string): Promise<ConversationStatus> => {
    return new Promise<ConversationStatus>((resolve, reject) => {
        contractConfigurationClient
            .paperAgentGetStatus(
                PaperAgentGetStatusInput.clone({
                    conversationId
                })
            )
            .subscribe(
                (response: PaperAgentGetStatusOutput) => {
                    resolve(response.status as ConversationStatus);
                },
                (error: UnknownCoralError) => {
                    console.error('Error getting conversation status:', error.receivedException.message);
                    reject(new Error(error.receivedException.message));
                }
            );
    });
};

/**
 * Poll for conversation status and messages after sending a user message
 * Uses paperAgentGetStatus to determine when AI processing is complete
 * @param conversationId Conversation ID to poll
 * @param lastKnownTimestamp Timestamp of the last known message to avoid duplicates
 * @param onNewMessages Callback for when new messages are received (display immediately)
 * @param onStatusChange Callback when status changes (WORKING/READY)
 * @param onComplete Callback when status becomes READY (stop polling)
 * @param onError Callback for errors
 * @param intervalMs Polling interval in milliseconds (default 5000ms = 5 seconds)
 * @returns Function to stop polling
 */
export const pollForStatusAndMessages = (
    conversationId: string,
    lastKnownTimestamp: string | number,
    onNewMessages: (messages: Message[]) => void,
    onStatusChange: (status: ConversationStatus) => void,
    onComplete: () => void,
    onError: (error: Error) => void,
    intervalMs = 5000
): (() => void) => {
    let isPolling = true;
    let timeoutId: NodeJS.Timeout;
    let lastProcessedTimestamp = timestampToNumber(lastKnownTimestamp);

    const poll = async () => {
        if (!isPolling) return;

        try {
            // First, check the status
            const status = await getConversationStatus(conversationId);
            onStatusChange(status);

            // Then, get any new messages
            const allMessages = await getConversation(conversationId);
            const newMessages = allMessages.filter((msg) => timestampToNumber(msg.timestamp) > lastProcessedTimestamp);

            if (newMessages.length > 0) {
                // Sort messages by timestamp to process in order
                const sortedNewMessages = newMessages.sort((a, b) => compareTimestamps(a.timestamp, b.timestamp));
                onNewMessages(sortedNewMessages);

                // Update last processed timestamp
                lastProcessedTimestamp = getMaxTimestamp(sortedNewMessages.map((msg) => msg.timestamp));
            }

            // Check if we should stop polling
            if (status === ConversationStatus.READY) {
                isPolling = false;
                onComplete();
                return;
            }

            // Schedule next poll if still working
            if (isPolling && status === ConversationStatus.WORKING) {
                timeoutId = setTimeout(poll, intervalMs);
            }
        } catch (error) {
            isPolling = false;
            onError(error instanceof Error ? error : new Error('Polling failed'));
        }
    };

    // Start polling
    timeoutId = setTimeout(poll, intervalMs);

    // Return stop function
    return () => {
        isPolling = false;
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    };
};
/**
 * Submit feedback for an AI response
 * @param conversationId Conversation ID
 * @param userId User ID
 * @param messageTimestamp Timestamp of the message being rated
 * @param feedbackType Type of feedback (GOOD or BAD)
 * @param feedbackText Optional feedback text
 * @param userRequest Original user request
 * @param agentResponse AI agent response
 * @returns Promise that resolves to success status
 */
export const submitFeedback = async (
    conversationId: string,
    userId: string,
    messageTimestamp: string,
    feedbackType: FeedbackType,
    feedbackText: string | undefined,
    userRequest: string,
    agentResponse: string
): Promise<boolean> => {
    return new Promise<boolean>((resolve, reject) => {
        contractConfigurationClient
            .paperAgentSubmitFeedback(
                PaperAgentSubmitFeedbackInput.clone({
                    conversationId,
                    userId,
                    messageTimestamp,
                    feedbackType,
                    feedbackText,
                    userRequest,
                    agentResponse
                })
            )
            .subscribe(
                (response: PaperAgentSubmitFeedbackOutput) => {
                    resolve(response.success);
                },
                (error: UnknownCoralError) => {
                    console.error('Error submitting feedback:', error.receivedException.message);
                    reject(new Error(error.receivedException.message));
                }
            );
    });
};
